<template>
  <div class="bar-chart">
    <v-chart ref="chart" :options="chartOption" autoresize style="width: 100%" />
  </div>
</template>

<script>
import ECharts from 'vue-echarts';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/grid';
import 'echarts/lib/chart/bar';
import useAppConfig from '@core/app-config/useAppConfig';
import { computed } from '@vue/composition-api';

export default {
  name: 'BarChartComponent',
  components: {
    VChart: ECharts
  },
  inheritAttrs: false,
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    isEmpty: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    categories: {
      type: Array,
      default: () => []
    },
    series: {
      type: Array,
      default: () => []
    },
    horizontal: {
      type: Boolean,
      default: false
    },
    valueMin: {
      type: Number,
      default: null
    },
    valueMax: {
      type: Number,
      default: null
    },
    valueInterval: {
      type: Number,
      default: null
    },
    stacked: {
      type: Boolean,
      default: false
    },
    colors: {
      type: Array,
      default: () => ['#7367F0', '#28C76F', '#EA5455', '#FF9F43', '#00CFE8']
    },
    height: {
      type: Number,
      default: 250
    },
    valueFormatter: {
      type: Function,
      default: null
    }
  },
  setup() {
    const { skin } = useAppConfig();
    const isDark = computed(() => skin.value === 'dark');

    return { isDark };
  },
  mounted() {
    this.$nextTick(() => {
      this.attachChartEvents();
    });
  },
  beforeDestroy() {
    const chart = this.getChartInstance();
    if (chart) chart.off('click');
  },
  computed: {
    chartOption() {
      const valueAxisBase = {
        type: 'value',
        boundaryGap: [0, 0.01],
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: this.isDark ? '#3b4253' : '#eee',
            type: 'dashed'
          }
        },
        axisLabel: {
          color: this.isDark ? '#b4b7bd' : '#6e6b7b'
        }
      };

      if (this.valueMin !== null) valueAxisBase.min = this.valueMin;
      if (this.valueMax !== null) valueAxisBase.max = this.valueMax;
      if (this.valueInterval !== null) valueAxisBase.interval = this.valueInterval;

      return {
        color: this.colors,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          textStyle: {
            fontSize: 12,
            color: this.isDark ? '#d0d2d6' : '#333'
          },
          backgroundColor: this.isDark ? 'rgba(40, 48, 70, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          borderColor: this.isDark ? '#3b4253' : '#eee',
          borderWidth: 1
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          width: '95%',
          top: this.series.length > 1 ? '40' : '20',
          containLabel: true
        },
        xAxis: this.horizontal
          ? valueAxisBase
          : {
              type: 'category',
              data: this.categories,
              axisLine: {
                lineStyle: {
                  color: this.isDark ? '#3b4253' : 'rgb(110, 107, 123)'
                }
              },
              axisTick: {
                show: false,
                alignWithLabel: true
              },
              axisLabel: {
                fontSize: 11,
                rotate: 30,
                color: this.isDark ? '#b4b7bd' : '#6e6b7b'
              }
            },
        yAxis: this.horizontal
          ? {
              type: 'category',
              data: this.categories,
              axisLine: {
                lineStyle: {
                  color: this.isDark ? '#3b4253' : '#dcdcdc'
                }
              },
              axisTick: {
                show: false,
                alignWithLabel: true
              },
              axisLabel: {
                fontSize: 11,
                color: this.isDark ? '#b4b7bd' : '#6e6b7b'
              }
            }
          : {
              ...valueAxisBase,
              axisLabel: {
                fontSize: 11,
                color: this.isDark ? '#b4b7bd' : '#6e6b7b'
              }
            },
        series: this.series.map(item => ({
          name: item.name,
          type: 'bar',
          stack: this.stacked ? 'total' : undefined,
          barMaxWidth: 30,
          emphasis: {
            focus: 'series'
          },
          label: {
            show: true,
            position: this.horizontal ? 'right' : 'top',
            fontSize: 10,
            color: this.isDark ? '#d0d2d6' : '#333',
            formatter: value => {
              if (this.valueFormatter) {
                return this.valueFormatter(value.data);
              }
              return value.data;
            }
          },
          data: item.data
        }))
      };
    }
  },
  methods: {
    getChartInstance() {
      const ref = this.$refs.chart;
      if (!ref) return null;
      if (typeof ref.getEchartsInstance === 'function') return ref.getEchartsInstance();
      return ref.chart || null;
    },
    attachChartEvents() {
      const chart = this.getChartInstance();
      if (!chart || this.barClickBound) return;
      this.barClickBound = true;
      chart.on('click', params => {
        this.$emit('bar-click', params);
      });
    }
  }
};
</script>

<style scoped>
.bar-chart {
  width: 100%;
  height: 100%;
  min-height: 250px;
}
</style>
